import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, memo } from 'react';
import { X, AtSign, Folder, Globe, Square } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { useMentionSources } from './useMentionSources';
import { AI_PREFILL_EVENT } from '../../services/ai/aiPrefill';
import { adjustChatInputHeight, buildReferenceOptions, filterCommands, filterReferenceOptions, refKey } from './chatInputHelpers';

export const ChatInput = memo(function ChatInput({
  onSend,
  onStop,
  onClearChat,
  isStreaming,
  isOpen,
  collections,
  environments,
  extensionActions,
}) {
  const [input, setInput] = useState('');
  const [attachedRefs, setAttachedRefs] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerIndex, setPickerIndex] = useState(0);
  const [pickerTrigger, setPickerTrigger] = useState('@');
  const textareaRef = useRef(null);
  const pickerRef = useRef(null);
  const inputShellRef = useRef(null);
  const wasStreamingRef = useRef(false);

  // Documents, boards and DB connections load on first picker open — see
  // useMentionSources for why they are lazy rather than loaded on mount.
  const extraOptions = useMentionSources({ enabled: pickerOpen, extensionActions });
  const allOptions = [...buildReferenceOptions(collections, environments), ...extraOptions];
  const pickerOptions = [
    ...(pickerTrigger === '/' ? filterCommands(pickerQuery) : []),
    ...filterReferenceOptions(allOptions, pickerQuery),
  ];

  useLayoutEffect(() => {
    adjustChatInputHeight(textareaRef.current);
  }, [input]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  useEffect(() => {
    if (isOpen) focusInput();
  }, [isOpen, focusInput]);

  // Prefill from anywhere in the app (services/ai/aiPrefill.js). Filling the
  // composer rather than sending is the default on purpose: an inline button
  // that spends the user's own API-key money on one click is a button people
  // learn not to press. `send: true` is opt-in per call site.
  useEffect(() => {
    const onPrefill = (event) => {
      const { text, references = [], send = false } = event.detail || {};
      if (!text) return;
      if (send) {
        onSend({ text, references });
        return;
      }
      setInput(text);
      setAttachedRefs(references);
      focusInput();
      // The textarea auto-grows; a multi-line prefill must not arrive clipped.
      requestAnimationFrame(() => adjustChatInputHeight(textareaRef.current));
    };
    window.addEventListener(AI_PREFILL_EVENT, onPrefill);
    return () => window.removeEventListener(AI_PREFILL_EVENT, onPrefill);
  }, [focusInput, onSend]);

  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      focusInput();
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, focusInput]);

  useEffect(() => {
    setPickerIndex(0);
  }, [pickerQuery, pickerOpen]);

  const stripActiveAtToken = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? input.length;
    const before = input.slice(0, cursor);
    const after = input.slice(cursor);
    const stripped = before.replace(/[@/]([^\s@/]*)$/, '');
    if (stripped !== before) {
      const next = stripped + after;
      setInput(next);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = stripped.length;
      });
    }
  }, [input]);

  const closePicker = useCallback((stripToken = false) => {
    setPickerOpen(false);
    setPickerQuery('');
    setPickerIndex(0);
    if (stripToken) stripActiveAtToken();
  }, [stripActiveAtToken]);

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const onPointerDown = (e) => {
      if (inputShellRef.current?.contains(e.target)) return;
      closePicker(true);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePicker(true);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pickerOpen, closePicker]);

  const addReference = useCallback((item) => {
    setAttachedRefs((prev) => {
      const key = refKey(item);
      if (prev.some((r) => refKey(r) === key)) return prev;
      return [...prev, item];
    });
    closePicker();
    textareaRef.current?.focus();
  }, [closePicker]);

  const removeReference = useCallback((key) => {
    setAttachedRefs((prev) => prev.filter((r) => refKey(r) !== key));
  }, []);

  const openPicker = useCallback((query = '') => {
    setPickerQuery(query);
    setPickerOpen(true);
  }, []);

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming) return;
    const refs = attachedRefs;
    setInput('');
    setAttachedRefs([]);
    closePicker();
    onSend({ text, references: refs });
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.style.height = 'auto';
        adjustChatInputHeight(el);
        el.focus();
      }
    });
  }, [input, isStreaming, onSend, attachedRefs, closePicker]);

  const handleChange = (e) => {
    const value = e.target.value;
    setInput(value);

    const cursor = e.target.selectionStart ?? value.length;
    const before = value.slice(0, cursor);
    const triggerMatch = before.match(/[@/]([^\s@/]*)$/);
    if (triggerMatch) {
      setPickerTrigger(triggerMatch[0][0]);
      setPickerQuery(triggerMatch[1]);
      setPickerOpen(true);
    } else {
      closePicker();
    }
  };

  const executePickerItem = useCallback((item) => {
    if (item.type === 'command') {
      stripActiveAtToken();
      closePicker();
      if (item.id === 'clear') onClearChat?.();
    } else {
      stripActiveAtToken();
      addReference(item);
    }
  }, [stripActiveAtToken, closePicker, addReference, onClearChat]);

  const pickHighlighted = () => {
    const item = pickerOptions[pickerIndex];
    if (!item) return;
    executePickerItem(item);
  };

  const handleKeyDown = (e) => {
    if (pickerOpen && pickerOptions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setPickerIndex((i) => (i + 1) % pickerOptions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setPickerIndex((i) => (i - 1 + pickerOptions.length) % pickerOptions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickHighlighted();
        return;
      }
    }
    if (e.key === 'Escape' && pickerOpen) {
      e.preventDefault();
      closePicker(true);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div ref={inputShellRef} className="flex-shrink-0 border-t border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 px-3 py-2">
      <div className="ai-chat-input-column w-full">
        {attachedRefs.length > 0 && (
          <div className="ai-chat-ref-chips">
            {attachedRefs.map((ref) => (
              <span key={refKey(ref)} className="ai-chat-ref-chip">
                {ref.type === 'request' && <span className="ai-chat-ref-chip-method">{ref.data?.method || 'GET'}</span>}
                {ref.type === 'collection' && <Folder size={11} className="flex-shrink-0 opacity-60" />}
                {ref.type === 'environment' && <Globe size={11} className="flex-shrink-0 opacity-60" />}
                <span className="truncate max-w-[10rem]">{ref.label}</span>
                <button
                  type="button"
                  className="ai-chat-ref-chip-remove"
                  onClick={() => removeReference(refKey(ref))}
                  aria-label={`Remove ${ref.label}`}
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="ai-chat-input-wrap relative">
          {pickerOpen && (
            <div ref={pickerRef} className="ai-chat-ref-picker panel-scroll">
              {pickerOptions.length === 0 ? (
                <div className="ai-chat-ref-picker-empty">No matches</div>
              ) : (
                pickerOptions.map((item, idx) => (
                  <button
                    key={`${item.type}:${item.id}`}
                    type="button"
                    className={`ai-chat-ref-picker-item${idx === pickerIndex ? ' ai-chat-ref-picker-item--active' : ''}${item.type === 'command' ? ' ai-chat-ref-picker-item--command' : ''}`}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => executePickerItem(item)}
                  >
                    <span className="ai-chat-ref-picker-type">{item.type === 'command' ? '/' : item.type}</span>
                    <span className="ai-chat-ref-picker-label">{item.label}</span>
                    <span className="ai-chat-ref-picker-meta">{item.meta}</span>
                  </button>
                ))
              )}
            </div>
          )}

          <div className="ai-chat-input-field">
            <Tooltip side="top" content="Add context (@ or /)">
              <button
                type="button"
                onClick={() => openPicker('')}
                disabled={isStreaming}
                className="ai-chat-ref-trigger"
                aria-label="Add context reference"
              >
                <AtSign size={15} />
              </button>
            </Tooltip>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask the AI assistant… (@ or / for context)"
              rows={1}
              className="ai-chat-input flex-1 resize-none border-0 bg-transparent py-2 pr-3 pl-1 text-sm text-gray-900 dark:text-zinc-100 placeholder:text-gray-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-0"
            />
            {/* Colours live in .ai-chat-stop-btn, not utilities: index.css is a
                hand-rolled utility subset and the zinc/hover classes this
                button used to carry did not exist in it — only `bg-gray-800`
                landed, which is Tailwind's blue-tinted gray, hence the bluish
                chip, and dark mode got no override at all. */}
            {isStreaming && (
              <Tooltip side="top" content="Stop (interrupt the assistant)">
                <button
                  type="button"
                  onClick={() => onStop?.()}
                  className="ai-chat-stop-btn"
                  aria-label="Stop generating"
                >
                  <Square size={12} strokeWidth={0} fill="currentColor" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
